import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScadenzaBadge } from "@/components/StatoBadge";
import { Mail, Loader2, BellRing } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/constants";

export default function Scadenze() {
  const { user } = useAuth();
  const isManager = ["admin", "coordinatore"].includes(user?.role);
  const [sending, setSending] = useState(null);

  const { data = [], isLoading, refetch } = useQuery({
    queryKey: ["scadenze"],
    queryFn: async () => (await api.get("/scadenze?giorni=60")).data,
  });

  const notify = async (s) => {
    setSending(`${s.guardia_id}-${s.tipo}`);
    try {
      await api.post("/scadenze/notify", {
        guardia_id: s.guardia_id, tipo: s.tipo, data_scadenza: s.data_scadenza, giorni: s.giorni,
      });
      toast.success(`Email inviata a ${s.nome}`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setSending(null); }
  };

  return (
    <div className="gz-fade-up">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">Avvisi scadenze</h1>
        <p className="mt-1 text-sm text-slate-500">{isManager ? "Nomine, tesserini e decreti in scadenza entro 60 giorni." : "Le tue scadenze (nomina, tesserino, decreto) in arrivo."}</p>
      </div>

      {isLoading ? (
        <p className="text-sm text-slate-400">Caricamento…</p>
      ) : data.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-12 text-center text-sm text-slate-400">
          <BellRing className="mx-auto mb-3 h-8 w-8 text-slate-300" />
          Nessuna scadenza imminente. Tutto in regola.
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {data.map((s, i) => (
            <Card key={i} data-testid={`scadenza-${s.guardia_id}-${s.tipo}`} className={`border-l-4 shadow-sm ${s.scaduto ? "border-l-red-500" : s.giorni <= 30 ? "border-l-amber-500" : "border-l-green-500"}`}>
              <CardContent className="flex flex-wrap items-center justify-between gap-3 p-5">
                <div>
                  <p className="font-heading font-bold text-slate-900">{s.nome}</p>
                  <p className="text-sm text-slate-500">{s.tipo} · scadenza {fmtDate(s.data_scadenza)} · {s.distaccamento || "—"}</p>
                </div>
                <div className="flex items-center gap-3">
                  <ScadenzaBadge giorni={s.giorni} />
                  {isManager && (
                    <Button size="sm" variant="outline" disabled={!s.email || sending === `${s.guardia_id}-${s.tipo}`} data-testid={`notify-${s.guardia_id}-${s.tipo}`} onClick={() => notify(s)}>
                      {sending === `${s.guardia_id}-${s.tipo}` ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Mail className="mr-1.5 h-4 w-4" />}
                      Avvisa
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
